import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, isSignInWithEmailLink, signInWithEmailLink, onAuthStateChanged } from 'firebase/auth';
import type { Auth, User, IdTokenResult } from 'firebase/auth';
import type { IFirebaseUser } from '@@@/types/firebase';

import { setAuthReady } from '@@@/composables/use-auth-ready';
import { sendAuthLink } from '@@@/composables/api/auth';

interface RuntimeConfigPublic {
    firebaseApiKey: string;
    firebaseAuthDomain: string;
    firebaseDatabaseURL: string;
}

interface NuxtProvide {
    firebaseApp: any;
    firebaseAuth: any;
}

export default defineNuxtPlugin((nuxtApp) => {
    const firebaseUser = useUser();
    const { public: publicRuntimeConfig } = useRuntimeConfig();

    const firebaseConfig = {
        apiKey: publicRuntimeConfig.firebaseApiKey,
        authDomain: publicRuntimeConfig.firebaseAuthDomain,
        databaseURL: publicRuntimeConfig.firebaseDatabaseURL,
    } as Partial<RuntimeConfigPublic>;

    const app = initFirebaseApp(firebaseConfig);
    const auth = getAuth(app);

    onAuthStateChanged(auth, async (user) => {
        if (user) {
            const token = await user.getIdTokenResult(true);

            updateFirebaseUser(firebaseUser, user, token);
        }

        setAuthReady(true);
    });

    nuxtApp.hooks.hook('app:mounted', () => {
        setupFirebaseAuthListener(auth, firebaseUser);
    });

    return {
        provide: {
            firebaseApp: app,
            firebaseAuth: auth,
            firebaseEmailLogin: async function (email: string, password: string) {
                const userCredential = await signInWithEmailAndPassword(auth, email, password);
                const user = userCredential.user;
                const token = await user.getIdTokenResult();

                updateFirebaseUser(firebaseUser, user, token);

                return firebaseUser;
            },
            firebaseMagicLinkRequest: async function (email: string) {
                const actionCodeSettings = {
                    url: `${window.location.origin}/login/`,
                    handleCodeInApp: true,
                };
                await sendAuthLink(email);
                window.localStorage.setItem('emailForSignIn', email);
            },

            firebaseCheckMagicLink: function (url: string) {
                return isSignInWithEmailLink(auth, url);
            },

            firebaseMagicLinkLogin: async function (url: string) {
                if (isSignInWithEmailLink(auth, url)) {
                    let email = window.localStorage.getItem('emailForSignIn');
                    if (!email) {
                        email = window.prompt('Please provide your email for confirmation');
                    }

                    const userCredential = await signInWithEmailLink(auth, email || '', url);

                    window.localStorage.removeItem('emailForSignIn');

                    const user = userCredential.user;
                    const token = await user.getIdTokenResult();

                    const userInfo = await getUserInfo();
                    if (firebaseUser.value) {
                        firebaseUser.value.firstName = userInfo.firstName;
                        firebaseUser.value.lastName = userInfo.lastName;
                    }

                    updateFirebaseUser(firebaseUser, user, token);

                    return firebaseUser;
                }
            },
            firebaseLogout: function () {
                // kill the refresh interval timer
                clearInterval(firebaseUser.value?.refreshTimerId);

                auth.signOut();
            }
        },
    };
});

function initFirebaseApp (firebaseConfig: Partial<RuntimeConfigPublic>) {
    // @ts-ignore
    return initializeApp(firebaseConfig);
}

function setupFirebaseAuthListener (auth: Auth, firebaseUser: Ref<IFirebaseUser | null>) {
    auth.onIdTokenChanged(async (currentUser: User | null) => {
        if (!currentUser) {
            firebaseUser.value = null;
            return;
        }

        try {
            const idTokenResult: IdTokenResult = await currentUser.getIdTokenResult();

            handleTokenExpiration(idTokenResult, currentUser, firebaseUser);
        } catch (error) {
            handleFirebaseTokenError(error);
        }
    });
}

async function handleTokenExpiration (idTokenResult: IdTokenResult, currentUser: User, firebaseUser: Ref<IFirebaseUser | null>) {
    const expiresIn: number = getTokenExpirationDuration(idTokenResult);
    const delay: number = getRefreshDelay(expiresIn);

    updateFirebaseUser(firebaseUser, currentUser, idTokenResult);

    if (!firebaseUser.value) {
        redirectToLogin();
    } else {
        // Update user info if available (wait for next tick to ensure firebaseUser is set)
        await nextTick();
        const userInfo = await getUserInfo();
        if (firebaseUser.value) {
            firebaseUser.value.firstName = userInfo.firstName;
            firebaseUser.value.lastName = userInfo.lastName;
        }

        refreshFirebaseToken(delay, currentUser, firebaseUser);
    }
}

function getTokenExpirationDuration (idTokenResult: IdTokenResult): number {
    return new Date(idTokenResult.expirationTime).getTime() - new Date().getTime();
}

function getRefreshDelay (expiresIn: number): number {
    return Math.max(0, expiresIn - 5 * 60 * 1000); // Refresh 5 minutes before expiration
}

function updateFirebaseUser (firebaseUser: Ref<IFirebaseUser | null>, currentUser: User, idTokenResult: IdTokenResult) {
    firebaseUser.value = {
        id: currentUser.uid,
        email: currentUser.email || '',
        token: idTokenResult,
        firstName: firebaseUser.value?.firstName || undefined,
        lastName: firebaseUser.value?.lastName || undefined,
        refreshTimerId: firebaseUser.value?.refreshTimerId || undefined,
    };
}

function redirectToLogin () {
    const route = useRoute();
    const router = useRouter();
    router.push({
        path: '/login/',
        query: { redirectUrl: route.fullPath, errorCode: 440 },
    });
}

function refreshFirebaseToken (delay: number, currentUser: User, firebaseUser: Ref<IFirebaseUser | null>) {
    if (firebaseUser.value?.refreshTimerId) {
        clearTimeout(firebaseUser.value.refreshTimerId);
    }

    const timerId = setTimeout(async () => {
        try {
            const refreshedToken: string | undefined = await currentUser.getIdToken(true);

            if (firebaseUser.value && refreshedToken) {
                firebaseUser.value.token.token = refreshedToken;
            }
        } catch (error) {
            handleFirebaseTokenError(error);
        }
    }, delay);

    if (firebaseUser.value) {
        firebaseUser.value.refreshTimerId = timerId;
    }
}

function handleFirebaseTokenError (error: any) {
    console.error(error); //eslint-disable-line
    return null;
}
